/**
 * WebSocket hook for real-time notifications
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../components/Auth/AuthProvider';
import { getAPIClient, getAPIBaseUrl } from '../api/client';
import { getUnreadCount, listNotifications } from '../api/notifications';

function getNotificationWebSocketBaseUrl(): string {
  try {
    const base = getAPIBaseUrl();
    return base.replace(/^http/, 'ws');
  } catch {
    return import.meta.env.VITE_API_URL?.replace(/^http/, 'ws') || 'ws://localhost:8000';
  }
}

export function useNotificationWebSocket() {
  const queryClient = useQueryClient();
  const { user, accessToken } = useAuthContext();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  useEffect(() => {
    // Skip WebSocket if no user info at all
    if (!user?.id && !user?.email) {
      return;
    }
    
    // For master key placeholder, we'll let the backend handle it via email lookup
    // But we still need a user_id for WebSocket, so skip if it's the placeholder
    if (user?.id === 'master-key-user') {
      return;
    }

    // Get auth token from API client or use accessToken from context
    const apiClient = getAPIClient();
    const token = accessToken || apiClient.getAuthToken();

    if (!token) {
      console.warn('[useNotificationWebSocket] No auth token available');
      return;
    }

    const connect = () => {
      try {
        // Use same base URL as REST API (runtime config in deployed app), not hardcoded localhost
        const wsBase = getNotificationWebSocketBaseUrl();
        const wsUrl = `${wsBase}/api/notifications/ws/notifications?token=${encodeURIComponent(token)}&user_id=${encodeURIComponent(user.id)}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          console.log('[useNotificationWebSocket] Connected');
          reconnectAttempts.current = 0;
          
          // Subscribe to user-specific topic
          ws.send(JSON.stringify({
            type: 'subscribe',
            topic: `/topic/notifications/user/${user.id}`
          }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'notification') {
              // Update React Query cache
              queryClient.setQueryData(['notifications', 'recent'], (old: any) => {
                if (!old) return { notifications: [data.notification], total: 1, unread_count: 1, page: 1, page_size: 10 };
                return {
                  ...old,
                  notifications: [data.notification, ...old.notifications],
                  unread_count: (old.unread_count || 0) + 1,
                };
              });
              
              // Invalidate unread count
              queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
              
              // Invalidate notification list
              queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
            }
          } catch (error) {
            console.error('[useNotificationWebSocket] Error parsing message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('[useNotificationWebSocket] WebSocket error:', error);
        };

        ws.onclose = () => {
          console.log('[useNotificationWebSocket] Disconnected');
          wsRef.current = null;
          
          // Attempt to reconnect
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // Exponential backoff, max 30s
            console.log(`[useNotificationWebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current}/${maxReconnectAttempts})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connect();
            }, delay);
          } else {
            console.warn('[useNotificationWebSocket] Max reconnect attempts reached');
          }
        };

        wsRef.current = ws;
      } catch (error) {
        console.error('[useNotificationWebSocket] Connection error:', error);
      }
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user?.id, queryClient]);
}

